// src/components/TasksPage.jsx
import React, { useState, useEffect } from 'react';
import TaskModal from './TaskModal';
import { apiFetch } from '../lib/api';
// P0.31 31.B.5/31.B.6 — the Active/Archived toggle and the one archive confirm.
import useArchivedView from '../hooks/useArchivedView';
import { confirmArchive } from '../lib/archiveConfirm';
import './TasksPage.css';
import DataTransferMenu from './DataTransferMenu';

const PRIORITY_OPTIONS = ['Urgent', 'High', 'Medium', 'Low'];
const STATUS_OPTIONS   = ['Suggested', 'Approved', 'In Progress', 'Done', 'Snoozed'];
const TYPE_OPTIONS     = ['Follow-up', 'Meeting Prep', 'Research', 'Proposal Work', 'Administrative'];
const TIME_OPTIONS     = ['15 min', '30 min', '45 min', '1 hour'];

const PRIORITY_COLOR = {
  // Urgency vocab (canonical, Quinn-emitted)
  'Do First':  'var(--dm-red)',
  'Do Today':  '#ea580c',
  'This Week': 'var(--dm-blue-vivid)',
  // Severity vocab (legacy, still in data)
  'Urgent':    '#ef4444',
  'High':      '#f97316',
  'Important': '#f97316',
  'Medium':    '#f59e0b',
  'Low':       'var(--dm-blue-mid)',
};
const PRIORITY_BG = {
  // Urgency vocab (canonical)
  'Do First':  'var(--dm-red-wash)',
  'Do Today':  '#fff7ed',
  'This Week': 'var(--dm-blue-light)',
  // Severity vocab (legacy)
  'Urgent':    'var(--dm-red-light)',
  'High':      '#fff7ed',
  'Important': '#fff7ed',
  'Medium':    'var(--dm-amber-light)',
  'Low':       'var(--dm-blue-light)',
};
const STATUS_CONFIG = {
  'Suggested':   { bg: '#fef9c3', color: '#854d0e' },
  'Approved':    { bg: 'var(--dm-blue-light)', color: 'var(--dm-blue-deep)' },
  'In Progress': { bg: 'var(--dm-purple-light)', color: '#6d28d9' },
  'Done':        { bg: 'var(--dm-green-wash)', color: '#16a34a' },
  'Snoozed':     { bg: 'var(--dm-grey-200)', color: '#64748b' },
  'Completed':   { bg: 'var(--dm-green-wash)', color: '#16a34a' },
};

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function isOverdue(dueDate) {
  if (!dueDate) return false;
  return new Date(dueDate) < new Date(new Date().toDateString());
}

// -- New Task Modal (uses TaskModal.css classes) --
function NewTaskModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', priority: 'Medium', status: 'Approved',
    taskType: '', timeNeeded: '', dueDate: ''
  });
  const [saving, setSaving] = useState(false);
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const resp = await apiFetch('/api/tasks/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, dueDate: form.dueDate || null }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Create failed');
      if (onCreated) onCreated();
      onClose();
    } catch (err) { alert('Error: ' + err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="tm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="tm-modal">
        <div className="tm-header">
          <div className="tm-header-left">
            <div className="tm-icon">✅</div>
            <div><div className="tm-header-label">New Task</div></div>
          </div>
          <button className="tm-close" onClick={onClose}>✕</button>
        </div>

        <div className="tm-section">
          <label className="tm-label">Task Name *</label>
          <textarea
            className="tm-textarea"
            value={form.name}
            onChange={e => set('name', e.target.value)}
            rows={2}
            placeholder="What needs to be done?"
            autoFocus
          />
        </div>

        <div className="tm-grid">
          <div className="tm-field">
            <label className="tm-label">Priority</label>
            <select className="tm-select" value={form.priority} onChange={e => set('priority', e.target.value)}>
              {PRIORITY_OPTIONS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="tm-field">
            <label className="tm-label">Status</label>
            <select className="tm-select" value={form.status} onChange={e => set('status', e.target.value)}>
              {STATUS_OPTIONS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="tm-field">
            <label className="tm-label">Task Type</label>
            <select className="tm-select" value={form.taskType} onChange={e => set('taskType', e.target.value)}>
              <option value="">-- none --</option>
              {TYPE_OPTIONS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="tm-field">
            <label className="tm-label">Time Needed</label>
            <select className="tm-select" value={form.timeNeeded} onChange={e => set('timeNeeded', e.target.value)}>
              <option value="">-- none --</option>
              {TIME_OPTIONS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="tm-field tm-field-full">
            <label className="tm-label">Due Date</label>
            <input type="date" className="tm-input" value={form.dueDate} onChange={e => set('dueDate', e.target.value)} />
          </div>
        </div>

        <div className="tm-actions">
          <div className="tm-actions-left" />
          <div className="tm-actions-right">
            <button className="tm-btn tm-btn-ghost" onClick={onClose}>Cancel</button>
            <button className="tm-btn tm-btn-save" onClick={handleCreate} disabled={saving || !form.name.trim()}>
              {saving ? 'Creating…' : 'Create Task'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// -- Task Card --
function TaskCard({ task, onClick, onNavigateAccount, onArchive, onRestore }) {
  const isDone  = task.status === 'Done' || task.status === 'Completed';
  const overdue = isOverdue(task.dueDate) && !isDone;
  const pColor  = PRIORITY_COLOR[task.priority] || '#94a3b8';
  const pBg     = PRIORITY_BG[task.priority]    || 'var(--dm-grey-200)';
  const sCfg    = STATUS_CONFIG[task.status]     || {};

  return (
    <div
      className={`task-card ${isDone ? 'task-card-done' : ''} ${overdue ? 'task-card-overdue' : ''}`}
      style={{ borderLeftColor: pColor }}
      onClick={onClick}
    >
      <div className="task-card-header">
        <h4 className={`task-card-name ${isDone ? 'task-card-name-done' : ''}`}>{task.name}</h4>
        {task.priority && (
          <span className="task-priority-badge" style={{ background: pBg, color: pColor }}>
            {task.priority}
          </span>
        )}
      </div>

      <div className="task-card-meta">
        {task.status    && <span className="task-status-badge" style={{ background: sCfg.bg, color: sCfg.color }}>{task.status}</span>}
        {task.taskType  && <span className="task-type-badge">{task.taskType}</span>}
        {task.timeNeeded && <span className="task-time-badge">⏱ {task.timeNeeded}</span>}
      </div>

      {task.accountName && (
        <div
          className={`task-account-name${task.accountId && onNavigateAccount ? ' task-account-link' : ''}`}
          onClick={task.accountId && onNavigateAccount ? (e) => { e.stopPropagation(); onNavigateAccount(task.accountId); } : undefined}
        >
          🏢 {task.accountName}
        </div>
      )}

      {task.dueDate && (
        <div className={`task-due-date ${overdue ? 'task-due-overdue' : ''}`}>
          📅 {formatDate(task.dueDate)}{overdue ? ' · Overdue' : ''}
        </div>
      )}

      {task.autoGenerated && <div className="task-selma-badge">✦ Selma</div>}

      <div className="task-card-footer">
        <span className="task-click-hint">Click to edit →</span>
        {/* P0.31 31.B.3/31.B.5 — archive and restore. api/tasks/delete.js has
            always been a soft delete and nothing could read the rows back: 125
            of 232 tasks in this tenant were archived and reachable from
            nowhere. */}
        {task.deletedAt ? (
          <button className="task-archive-btn"
                  onClick={e => { e.stopPropagation(); onRestore && onRestore(task); }}>
            ↩ Restore
          </button>
        ) : onArchive ? (
          <button className="task-archive-btn"
                  onClick={e => { e.stopPropagation(); onArchive(task); }}>
            Archive
          </button>
        ) : null}
      </div>
    </div>
  );
}

// -- Main TasksPage --
export default function TasksPage({ onNavigateAccount }) {
  const [tasks, setTasks]                   = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [search, setSearch]                 = useState('');
  const [filterStatus, setFilterStatus]     = useState('active');
  const [filterPriority, setFilterPriority] = useState('all');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedTask, setSelectedTask]     = useState(null);
  const [showNewModal, setShowNewModal]     = useState(false);

  // P0.31 31.B.5 — 125 of this tenant's 232 tasks were archived and reachable
  // from nowhere. More than half the table.
  const archive = useArchivedView();

  // `archive.refetchKey` IS the dependency. fetchTasks is a plain function rather
  // than a useCallback, so it reads the hook's CURRENT value on every call — but
  // the effect still has to re-run when the toggle flips, or the view does not
  // change and the button looks broken. That exact stale-closure bug shipped in
  // SignalsPage earlier in this phase.
  useEffect(() => { fetchTasks(); }, [archive.refetchKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchTasks() {
    setLoading(true); setError(null);
    try {
      const res  = await apiFetch('/api/tasks/list?' + archive.param.replace(/^&/, ''));
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to fetch');
      setTasks(data.tasks || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  // Archive and restore, from the page the user is on.
  const archiveTask = async (task) => {
    if (!confirmArchive({ entity: 'task', name: task.name || task.title })) return;
    try {
      const res = await apiFetch('/api/tasks/delete', {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: task.id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to archive');
      setTasks(prev => prev.filter(t => t.id !== task.id));
    } catch (err) { alert('Could not archive that task: ' + err.message); }
  };

  const restoreTask = async (task) => {
    try {
      const res = await apiFetch('/api/tasks/unarchive', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: task.id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to restore');
      setTasks(prev => prev.filter(t => t.id !== task.id));
    } catch (err) { alert('Could not restore that task: ' + err.message); }
  };

  const isDone = t => t.status === 'Done' || t.status === 'Completed';

  const filtered = tasks.filter(t => {
    if (filterStatus === 'active' && isDone(t)) return false;
    if (filterStatus === 'done'   && !isDone(t)) return false;
    if (filterPriority !== 'all'  && t.priority !== filterPriority) return false;
    if (search && !(t.name || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Band-aid: include both urgency vocab (Do First/Do Today/This Week — canonical) AND
  // legacy severity vocab (Urgent/High/Important/Medium/Low — pre-canonical, still in data).
  // Time-aware priority workstream will resolve the dual-vocabulary collision.
  const GROUP_ORDER = [
    'Do First', 'Do Today', 'This Week',
    'Urgent', 'High', 'Important',
    'Medium', 'Low',
    'No Priority',
  ];
  const grouped = filtered.reduce((acc, task) => {
    const g = task.priority || 'No Priority';
    if (!acc[g]) acc[g] = [];
    acc[g].push(task);
    return acc;
  }, {});

  function isExpanded(name) { return expandedGroups[name] !== false; }
  function toggleGroup(name) { setExpandedGroups(prev => ({ ...prev, [name]: !isExpanded(name) })); }

  const activeTasks  = tasks.filter(t => !isDone(t));
  const doneTasks    = tasks.filter(t => isDone(t));
  const overdueTasks = activeTasks.filter(t => isOverdue(t.dueDate));

  return (
    <div className="tasks-page">

      <div className="tasks-header">
        <div className="tasks-header-left">
          <div className="tasks-title-row">
            <h1 className="tasks-title">Tasks</h1>
            {activeTasks.length > 0 && <span className="tasks-count">{activeTasks.length}</span>}
          </div>
          <div className="tasks-stats">
            <span className="stat-chip stat-active">{activeTasks.length} active</span>
            {overdueTasks.length > 0 && <span className="stat-chip stat-overdue">⚠ {overdueTasks.length} overdue</span>}
            <span className="stat-chip stat-done">{doneTasks.length} completed</span>
          </div>
        </div>
        <div className="tasks-header-right">
          <DataTransferMenu entity="tasks" label="Tasks" onImported={fetchTasks} />
          {/* P0.31 31.B.5 — the Archived view, same control on every entity. */}
          <button className={`btn-refresh${archive.showArchived ? ' btn-refresh-on' : ''}`}
                  onClick={archive.toggle} title={archive.button.title}>
            {archive.button.label}
          </button>
          <button className="btn-refresh" onClick={fetchTasks}>↻ Refresh</button>
          <button className="btn-new-task" onClick={() => setShowNewModal(true)}>+ New Task</button>
        </div>
      </div>

      <div className="tasks-filters">
        <input
          className="search-input"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search tasks..."
        />
        <div className="filter-tabs">
          {[['active','Active'],['done','Completed'],['all','All']].map(([val, lbl]) => (
            <button key={val} className={`filter-tab ${filterStatus === val ? 'filter-tab-active' : ''}`} onClick={() => setFilterStatus(val)}>
              {lbl}
            </button>
          ))}
        </div>
        <select className="filter-select" value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
          <option value="all">All Priorities</option>
          {PRIORITY_OPTIONS.map(p => <option key={p}>{p}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="tasks-loading">Loading tasks...</div>
      ) : error ? (
        <div className="tasks-loading" style={{ color: 'var(--dm-red)' }}>
          ⚠ {error} <button onClick={fetchTasks} style={{ marginLeft: 8, cursor: 'pointer' }}>Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="tasks-empty">
          <p>{filterStatus === 'done' ? 'No completed tasks.' : 'No active tasks.'}</p>
        </div>
      ) : filterStatus === 'done' ? (
        <div className="tasks-flat-grid">
          {filtered.map(task => (
            <TaskCard key={task.id} task={task} onClick={() => setSelectedTask(task)} onNavigateAccount={onNavigateAccount}
                        onArchive={archiveTask} onRestore={restoreTask} />
          ))}
        </div>
      ) : (
        <div className="tasks-groups">
          {GROUP_ORDER.map(group => {
            const items = grouped[group] || [];
            if (!items.length) return null;
            const expanded     = isExpanded(group);
            const color        = PRIORITY_COLOR[group] || '#94a3b8';
            const bg           = PRIORITY_BG[group]    || 'var(--dm-grey-200)';
            const overdueCount = items.filter(t => isOverdue(t.dueDate)).length;
            return (
              <div key={group} className="task-group">
                <div className="task-group-header" onClick={() => toggleGroup(group)}>
                  <div className="task-group-left">
                    <div className="priority-avatar" style={{ background: bg, color }}>
                      {group === 'Do First' ? '🔴' : group === 'Do Today' ? '🟡' : group === 'This Week' ? '🔵' : '⚪'}
                    </div>
                    <span className="task-group-name" style={{ color }}>{group}</span>
                    <span className="task-group-count">{items.length}</span>
                    {overdueCount > 0 && <span className="task-overdue-badge">{overdueCount} overdue</span>}
                  </div>
                  <span className="task-group-chevron">{expanded ? '▾' : '▸'}</span>
                </div>
                {expanded && (
                  <div className="tasks-cards-grid">
                    {items.map(task => (
                      <TaskCard key={task.id} task={task} onClick={() => setSelectedTask(task)} onNavigateAccount={onNavigateAccount}
                        onArchive={archiveTask} onRestore={restoreTask} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Real TaskModal -- same component as Account Detail */}
      {selectedTask && (
        <TaskModal
          task={selectedTask}
          onClose={() => { setSelectedTask(null); fetchTasks(); }}
          onSave={() => fetchTasks()}
          onDelete={() => { setSelectedTask(null); fetchTasks(); }}
          onNavigateAccount={onNavigateAccount}
        />
      )}

      {showNewModal && (
        <NewTaskModal onClose={() => setShowNewModal(false)} onCreated={fetchTasks} />
      )}
    </div>
  );
}
